// ============================================================================
// Universal Turnover Task Modal context
// Single entry point: openTurnoverTaskModal({ mode, propertyId, ... })
// ============================================================================

import { createContext, useCallback, useContext, useState } from "react";

export type TurnoverTaskModalMode = "create" | "edit";

export interface OpenTurnoverTaskModalParams {
  mode: TurnoverTaskModalMode;
  propertyId: string;
  /** Edit: task to edit */
  taskId?: string;
  /** Create: reservation to attach (sets default date + reservation times) */
  reservationId?: string;
  /** Create: initial cleaning date when no reservation (e.g. calendar day click) */
  initialDate?: Date;
}

interface TurnoverTaskModalState extends OpenTurnoverTaskModalParams {
  open: boolean;
}

interface TurnoverTaskModalContextValue {
  state: TurnoverTaskModalState;
  openTurnoverTaskModal: (params: OpenTurnoverTaskModalParams) => void;
  closeTurnoverTaskModal: () => void;
}

const defaultState: TurnoverTaskModalState = {
  open: false,
  mode: "create",
  propertyId: "",
};

const TurnoverTaskModalContext = createContext<TurnoverTaskModalContextValue | null>(null);

export function TurnoverTaskModalProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TurnoverTaskModalState>(defaultState);

  const openTurnoverTaskModal = useCallback((params: OpenTurnoverTaskModalParams) => {
    setState({
      open: true,
      mode: params.mode,
      propertyId: params.propertyId,
      taskId: params.taskId,
      reservationId: params.reservationId,
      initialDate: params.initialDate,
    });
  }, []);

  const closeTurnoverTaskModal = useCallback(() => {
    setState((prev) => (prev.open ? { ...prev, open: false } : prev));
  }, []);

  return (
    <TurnoverTaskModalContext.Provider
      value={{
        state,
        openTurnoverTaskModal,
        closeTurnoverTaskModal,
      }}
    >
      {children}
    </TurnoverTaskModalContext.Provider>
  );
}

export function useTurnoverTaskModal() {
  const ctx = useContext(TurnoverTaskModalContext);
  if (!ctx) {
    throw new Error("useTurnoverTaskModal must be used within TurnoverTaskModalProvider");
  }
  return ctx;
}

/** Optional hook for components that may render outside provider (e.g. team dashboard). Returns null if no provider. */
export function useTurnoverTaskModalOptional(): TurnoverTaskModalContextValue | null {
  return useContext(TurnoverTaskModalContext);
}
